import React from 'react';
import { message, Spin, Upload, Button, Input, Form, Table, Drawer } from 'antd';

import { TPharmacy } from 'types/common';
import { ColumnType } from 'antd/es/table';
import useMobileInfo from 'hooks/useMobileInfo';
import s from './s.module.less';
import { searchPharmacy } from 'api/prospect';

const { Dragger } = Upload;
const REQUIRE_FIELD_TIPS = "This is a required field";

export type TPharmacyScriptSure = {
    services: string[]; //["New", "Refill", "Cancel", "Controlled Substance"];
    specialties: string[]; //["Retail", "Supports Digital Signature", "Dispenses For Humans"
    npi: string; //"1245246826";
    businessName: string; //"WALGREENS DRUG STORE #01120";
    addressLine1: string; //"4645 MISSION ST";
    addressLine2: string; //"";
    city: string; //"SAN FRANCISCO";
    stateProvince: string; //"CA";
    postalCode: string; //"941122605";
    primaryTelephone: string; //"4155856900";
    fax: string; //"4155851524";
    electronicMail: string; //"";
    alternatePhoneNumbers: string; //"";
    activeStartDate: "2024-06-10";
    activeEndDate: string; //"2099-12-31";
    serviceLevel: string; //"New~Refill~Cancel~ControlledSubstance";
    lastModifiedDate: string; //"2025-02-28T17:45:37.0Z";
    crossStreet: string; //"MISSION & OCEAN AVENUE";
    directorySpecialtyName: string; //"Retail~SupportsDigitalSignature~DispensesForHumans";
    directAddress: string; //"";
    latitude: string; //"37.723928";
    longitude: string; //"-122.4349";
    precise: string; //"Y";
    ncpdpId: string; //"0576857";
    countryCode: string; //"US";
    version: string; //"v6_1_erx";
};

const transferPharmacyScriptSureToPharmacy = (
    data: TPharmacyScriptSure[]
): TPharmacy[] => {
    return data.map((item) => {
        return {
            zipCode: item.postalCode,
            name: item.businessName,
            address: `${item.addressLine1} ${
                item.addressLine2 ? item.addressLine2 : ""
            } ${item.crossStreet ? item.crossStreet : ""}`,
            city: item.city,
            state: item.stateProvince,
            phone: item.primaryTelephone,
            ncpDpId: item.ncpdpId,
        };
    });
};


type PharmacySelectProps = {
    value?: TPharmacy[];
    onChange?: (val: TPharmacy[]) => void;
};

type TForm = {
    zip: string;
    pharmacyName: string;
};

const PharmacySelect = (Props: PharmacySelectProps) => {
    const { value, onChange } = Props;
    const [loading, setLoading] = React.useState(false);
    const [showResult, setShowResult] = React.useState(false);
    const [form] = Form.useForm<TForm>();
    const [pharmacyName, setPharmacyName] = React.useState("");
    const [data, setData] = React.useState<TPharmacy[]>([]);
    const [zip, setZip] = React.useState("");
    const [selectedPharmacy, setSelectedPharmacy] = React.useState<TPharmacy>();
    const [showViewMode, setShowViewMode] = React.useState(false);
    const [isMobile] = useMobileInfo();
    const [showPopMobileLayer, setShowPopMobileLayer] = React.useState(false);
    const [pendingSelectPharmacy, setPendingSelectPharmacy] = React.useState<TPharmacy>();//mobile
    const [formValues, setFormValues] = React.useState<TForm>();
    React.useEffect(() => {
        if (value && value.length > 0) {
            setShowViewMode(true);
            setSelectedPharmacy(value[0]);
        }
    }, [value]);

    const handleSearch = async () => {
        try {
            if (!formValues?.pharmacyName && !formValues?.zip) {
                await form.validateFields();
            }
            const vals = formValues;
            //remove form error
            form.setFields([{ name: 'zip', errors: [] }, { name: 'pharmacyName', errors: [] }]);
            if (isMobile) {
                setShowPopMobileLayer(true);
            }
            setLoading(true);
            const res = await searchPharmacy(vals?.pharmacyName, vals?.zip);
            if (!res.error) {
                const _data: TPharmacyScriptSure[] = res.data?.data || [];
                const _pharmacyData = transferPharmacyScriptSureToPharmacy(_data);
                setData(_pharmacyData);
                setShowResult(true);
            }
        } catch (e) {
            console.log({
                e,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setZip("");
        setPharmacyName("");
        setShowResult(false);
        if (selectedPharmacy) {
            setShowViewMode(true);
        }
    };

    const columns: ColumnType<TPharmacy>[] = [
        {
            title: "Pharmacy Name",
            dataIndex: "name",
            width: 200,
            key: "name",
            sorter: (a: TPharmacy, b: TPharmacy) => {
                return a.name.localeCompare(b.name);
            },
        },
        {
            title: "Address",
            dataIndex: "address",
            key: "address",
            width: 200,
        },
        {
            title: "City",
            dataIndex: "city",
            key: "city",
            width: 150
        },
        {
            title: "State",
            dataIndex: "state",
            key: "state",
            width: 150,
        },
        {
            title: "ZIP",
            dataIndex: "zipCode",
            key: "zipCode",
            width: 100,
        },
        {
            title: "Phone",
            dataIndex: "phone",
            key: "phone",
            width: 200,
        },
        {
            title: "",
            key: "action",
            fixed: "right",
            width: 100,
            render: (record: TPharmacy) => {
                return (
                    <div className={s.action}>
                        <Button
                            onClick={() => {
                                onChange?.([record]);
                                form.resetFields();
                                setZip("");
                                setPharmacyName("");
                                setSelectedPharmacy(record);
                                setShowResult(false);
                            }}
                            style={{ color: '#0E62F4', fontWeight: '500' }}
                            type="text"
                        >Select
                        </Button>
                    </div>
                );
            }
        },
    ];

    return (
        <div className={s.wrap}>
            {
                (!showViewMode || isMobile) && (
                    <Form
                        onValuesChange={(_val, allValues) => {
                            setFormValues(allValues);
                        }}
                        form={form}
                        layout="vertical"
                        className={s.form}
                    >
                        <div className={s.inner}>
                            <div className={s.row}>
                                <div className={s.rowItem}>
                                    <Form.Item
                                        label="ZIP code"
                                        name="zip"
                                        rules={[
                                            {
                                                required: true,
                                                message: REQUIRE_FIELD_TIPS,
                                            },
                                        ]}
                                    >
                                        <Input
                                            onChange={(e) => setZip(e.target.value)}
                                            size="large"
                                            style={{
                                                width: isMobile ? '100%' : '200px',
                                            }}
                                            value={zip}
                                            placeholder="Enter your ZIP code"
                                            className={s.input}
                                        />
                                    </Form.Item>
                                </div>
                                <div className={s.rowItem}>
                                    <Form.Item
                                        label="Pharmacy name"
                                        name="pharmacyName"
                                        style={{ width: isMobile ? '100%' : '460px' }}
                                        rules={[
                                            {
                                                required: true,
                                                message: REQUIRE_FIELD_TIPS,
                                            },
                                        ]}
                                    >
                                        <Input
                                            onChange={(e) => setPharmacyName(e.target.value)}
                                            size="large"
                                            style={{ width: isMobile ? '100%' : '460px' }}
                                            value={pharmacyName}
                                            placeholder="e.g., CVS, Walgreens"
                                            className={s.input}
                                        />
                                    </Form.Item>
                                </div>
                                {
                                    !isMobile && (
                                        <div className={s.rowItem}>
                                            <div className={s.btnWrap}>
                                                <Button
                                                    onClick={handleSearch}
                                                    type="primary"
                                                    className={s.search}
                                                    loading={loading}
                                                >
                                                    Search
                                                </Button>
                                                {
                                                    !loading && (showResult || !!selectedPharmacy) && (
                                                        <Button
                                                            onClick={handleCancel}
                                                            className={s.cancel}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    )
                                                }
                                            </div>
                                        </div>
                                    )
                                }
                                {
                                    isMobile && (
                                        <div className={s.rowItem}>
                                            <div className={s.btnWrap}>
                                                <Button
                                                    onClick={handleSearch}
                                                    type="primary"
                                                    size="large"
                                                    className={s.search}
                                                    loading={loading}
                                                    block
                                                >
                                                    Search
                                                </Button>
                                            </div>
                                        </div>
                                    )
                                }

                            </div>
                            {
                                !isMobile && (
                                    <div className={s.tableWrap}>
                                        {
                                            loading && (
                                                <div className={s.loading}>
                                                    <Spin />
                                                </div>
                                            )
                                        }
                                        {
                                            !loading && showResult && (
                                                <Table
                                                    className={`commonTable ${s.paymentTable}`}
                                                    columns={columns}
                                                    dataSource={data}
                                                    rowKey="ncpDpId"
                                                    // pagination={{
                                                    //     pageSize: 10,
                                                    //     showSizeChanger: true,
                                                    //     showQuickJumper: true,
                                                    //     showTotal: (total, range) =>
                                                    //         `${range[0]}-${range[1]} of ${total} items`,
                                                    // }}
                                                    pagination={{
                                                        pageSize: 5,
                                                        size: "small",
                                                        showSizeChanger: false,
                                                        showQuickJumper: false,
                                                    }}
                                                    scroll={{ x: "max-content" }}
                                                />
                                            )
                                        }
                                    </div>
                                )
                            }
                        </div>
                    </Form>
                )
            }
            {
                showViewMode && (
                    <div className={s.editWrap}>
                        <div className={s.left}>
                            <div className={s.label}>Preferred Pharmacy</div>
                            <div className={s.name}>{selectedPharmacy?.name}</div>
                            <div className={s.address}>
                                {`${selectedPharmacy?.address}, ${selectedPharmacy?.city}, ${selectedPharmacy?.state}, ${selectedPharmacy?.zipCode}`}
                            </div>
                            <div className={s.phone}>Phone:{selectedPharmacy?.phone}</div>
                        </div>
                        <div className={s.right}>
                            <Button className={s.edit} onClick={() => setShowViewMode(false)}>Edit</Button>
                        </div>
                    </div>
                )
            }
            <Drawer
                placement="bottom"
                open={showPopMobileLayer}
                onClose={() => {
                    setShowPopMobileLayer(false);
                    setPendingSelectPharmacy(undefined);
                }}
                title={null}
                rootClassName={s.drawerWrap}
                height="70vh"
            >
                {
                    loading ? (
                        <div className={s.loading}>
                            <Spin />
                        </div>
                    )
                        :
                        <div className={s.popMobileLayer}>
                            <div className={s.header}>
                                <span>Select your preferred pharmacy</span>
                                <span
                                    onClick={() => setShowPopMobileLayer(false)}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                        <path d="M6 18L18 6M6 6L18 18" stroke="#4B5563" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </div>
                            <div className={s.list}>
                                {
                                    data.map((item) => (
                                        <div
                                            onClick={() => {
                                                setPendingSelectPharmacy(item);
                                            }}
                                            className={`${s.popMobileLayerItem} ${pendingSelectPharmacy?.ncpDpId === item.ncpDpId ? s.active : ''}`}
                                            key={item.ncpDpId}
                                        >
                                            <div className={s.popMobileLayerItemName}>{item.name}</div>
                                            <div className={s.popMobileLayerItemAddress}>
                                                {item.address}, {item.city}, {item.state}, {item.zipCode}
                                            </div>
                                            <div className={s.popMobileLayerItemPhone}>
                                                Phone: {item.phone}
                                            </div>
                                        </div>
                                    ))
                                }
                            </div>
                            <div className={s.footer}>
                                <Button
                                    type="primary"
                                    size="large"
                                    className={s.btn}
                                    block
                                    onClick={() => {
                                        if (pendingSelectPharmacy) {
                                            setSelectedPharmacy(pendingSelectPharmacy);
                                            setShowPopMobileLayer(false);
                                            onChange?.([pendingSelectPharmacy]);
                                            form.resetFields();
                                            setZip("");
                                            setPharmacyName("");
                                            setShowResult(false);
                                        }
                                    }}
                                    disabled={!pendingSelectPharmacy}
                                >Select
                                </Button>
                            </div>
                        </div>
                }
            </Drawer>
        </div>
    );
};

export default PharmacySelect;
